import type { ReactNode } from 'react';
import styled from 'styled-components';
import { Flex } from 'antd';
import { BookmarkIcon } from '@phosphor-icons/react';
import { CollapsibleSection } from '../../CollapsibleSection';
import { Spinner } from '../../Spinner';
import { Tabs, type TabItem, TabBadgeStatus } from '../../Tabs';
import { colors, spacing } from '../../../theme-v2';

const ICON_COLOR = '#22D3EE';
const VIDEO_TAB_KEY = 'video';

export interface KnowledgeHubTabItem {
  /** Stable identifier for the tab. */
  key: string;
  /** Tab label (consumer-translated). */
  label: ReactNode;
  /** Icon shown beside the label. */
  icon?: ReactNode;
  /** Tab content rendered when active. */
  content: ReactNode;
  /** Disable this tab. */
  disabled?: boolean;
  /** Status indicator next to the label: generating, or ready-but-unread. */
  badgeStatus?: TabBadgeStatus;
}

export interface KnowledgeHubPanelProps {
  /** Section title (e.g. "Knowledge Hub"), consumer-translated. A lone
   *  Video add-on replaces it with its own label. */
  title: ReactNode;
  /** Add-on tabs (Video, Audio, Key points, Quiz, …). */
  tabs: KnowledgeHubTabItem[];
  /** Active tab key (controlled). */
  activeKey?: string;
  /** Default active tab key (uncontrolled). */
  defaultActiveKey?: string;
  /** Fired when the user switches tabs. */
  onTabChange?: (key: string) => void;
  /**
   * Fired on every tab click, including a click on the already-active tab
   * (which `onTabChange` ignores). Lets a consumer react to "the user chose
   * this add-on" even when the selection itself doesn't change.
   */
  onTabClick?: (key: string) => void;
  /**
   * Show a spinner instead of the tab strip while the consumer works out
   * which add-ons this experience has. Without it a consumer that hides the
   * panel on an empty `tabs` would unmount it for the duration of that
   * lookup, reflowing the whole sidebar and then reflowing back.
   */
  loading?: boolean;
  /** Whether the section starts expanded. Defaults to true. */
  defaultOpen?: boolean;
  /** When false, no collapse toggle is offered. `ExperiencePageLayout`
   *  injects this: with no chatbot beside it, collapsing has nowhere to go. */
  collapsible?: boolean;
  /** Controlled open state (passthrough to CollapsibleSection). */
  open?: boolean;
  /** Fired when the user toggles the section. */
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

/**
 * KnowledgeHubPanel — top half of the experience-page sidebar.
 * Composes `CollapsibleSection` (the "Knowledge Hub" disclosure) +
 * `Tabs` (the add-on strip) for the add-ons (Video,
 * Audio, Key points, Quiz, Trivia, Bookmarks, Quick recall, Notes…).
 *
 * Stateless aside from internal Tabs state when uncontrolled. Apps
 * pass `tabs` with the add-on `content` already rendered in each
 * item; this component owns only the framing and selection.
 *
 * A lone Video add-on takes over the header — its label and icon replace
 * "Knowledge Hub" — and drops the one-tab strip (MED-6989). Still
 * collapsible, so the split with the Chatbot is unaffected.
 */
export const KnowledgeHubPanel = ({
  title,
  tabs,
  activeKey,
  defaultActiveKey,
  onTabChange,
  onTabClick,
  loading = false,
  defaultOpen = true,
  collapsible = true,
  open,
  onOpenChange,
  className,
}: KnowledgeHubPanelProps) => {
  // Not while `loading`: `tabs` is provisional then, so renaming off it
  // would flash the title.
  const soleVideoTab =
    !loading && tabs.length === 1 && tabs[0]?.key === VIDEO_TAB_KEY
      ? tabs[0]
      : undefined;

  const tabItems: TabItem[] = tabs.map((t) => ({
    key: t.key,
    label: t.label,
    icon: t.icon,
    // Own scroller per tab, so one tab's offset never carries into another.
    children: <StyledTabScroll>{t.content}</StyledTabScroll>,
    disabled: t.disabled,
    badgeStatus: t.badgeStatus,
  }));

  return (
    <CollapsibleSection
      title={soleVideoTab?.label ?? title}
      icon={
        soleVideoTab?.icon ? (
          <StyledVideoIcon>{soleVideoTab.icon}</StyledVideoIcon>
        ) : (
          <BookmarkIcon size={14} weight="fill" color={ICON_COLOR} />
        )
      }
      defaultOpen={defaultOpen}
      collapsible={collapsible}
      open={open}
      onOpenChange={onOpenChange}
      className={className}
    >
      <StyledBody>
        {loading ? (
          <StyledLoading>
            <Spinner />
          </StyledLoading>
        ) : soleVideoTab ? (
          // Stands in for the tab pane when the strip is gone.
          <Flex
            vertical
            flex="1 1 auto"
            style={{ minHeight: 0, overflow: 'auto', padding: spacing.base }}
          >
            {soleVideoTab.content}
          </Flex>
        ) : (
          // Mounted only once `tabs` is final: AntD reads `defaultActiveKey`
          // at mount, so seeding it from a provisional list would leave the
          // uncontrolled selection pointing at a tab that gets filtered out.
          <Tabs
            variant="line"
            size="compact"
            scrollArrows
            items={tabItems}
            activeKey={activeKey}
            defaultActiveKey={defaultActiveKey ?? tabs[0]?.key}
            onChange={onTabChange}
            onTabClick={onTabClick}
          />
        )}
      </StyledBody>
    </CollapsibleSection>
  );
};

/* Tab icons are drawn at 18px — too big next to the 14px bookmark they
   replace in the header. */
const StyledVideoIcon = styled.span`
  display: inline-flex;
  color: ${ICON_COLOR};

  svg {
    width: 14px;
    height: 14px;
  }
`;

/* Holds the sidebar's height while the consumer resolves its add-ons, so the
   panel doesn't collapse to its header and reflow the split. */
const StyledLoading = styled.div`
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  min-height: 120px;
`;

/* The only scroller of a tab. Add-ons never scroll themselves: a flex chain
   down to their AddonLayout root lets them fill the tab or grow past it, so
   the bottom padding always ends below the content. */
const StyledTabScroll = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  /* The tab strip above runs edge-to-edge; this inset belongs to each
     add-on's own content, not the strip. Top padding is 0, not spacing.base
     like the other three sides: the shared Tabs component's scrollArrows
     wrapper already adds its own margin-bottom: spacing.base below the nav
     (see Tabs.tsx's StyledScrollableTabBar) — padding here on top of that
     would make the gap above the add-on's card bigger than the other three
     sides instead of matching them. */
  padding: 0 ${spacing.base}px ${spacing.base}px;
`;

const StyledBody = styled.div`
  display: flex;
  flex-direction: column;
  /* Grow to fill the CollapsibleSection body (now a flex column). */
  flex: 1 1 auto;
  min-height: 0;

  /* Make the AntD Tabs container fill the body and let its content
     pane absorb any remaining vertical space. */
  .ant-tabs {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
  }
  /* Hand the remaining height down to the active tab's StyledTabScroll. */
  .ant-tabs-content-holder,
  .ant-tabs-content,
  .ant-tabs-tabpane-active {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
  }

  /* Force theme-v2 accent on the active ink bar + active tab text
     regardless of any surrounding AntD ConfigProvider context (libs/ui
     Storybook ships with the legacy libertify theme). */
  .ant-tabs-ink-bar {
    background: ${colors.accent.base};
  }
  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${colors.accent.base};
  }

  .ant-tabs-tab.ant-tabs-tab-active {
    background: ${colors.accent.tint};
  }
  .ant-tabs-tab:not(.ant-tabs-tab-active):hover .ant-tabs-tab-btn {
    color: ${colors.accent.base};
  }
`;
